import { forwardRef, useCallback, useState } from "react";
import { Loader, Check, ChevronsDownUp, X } from "lucide-react";

import { cn, debounce } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";

export type ComboboxOption<T = unknown> = {
  value: string | number;
  label: string;
  raw?: T;
};

export interface ComboboxAsyncProps {
  buttonClassName?: string;
  boxWrapperClassName?: string;
  boxInputClassName?: string;
  boxListClassName?: string;
  boxGroupClassName?: string;
  boxItemClassName?: string;
  options: ComboboxOption[];
  value?: string;
  buttonPlaceholder?: string;
  inputPlaceholder?: string;
  onChange?: (option: ComboboxOption) => void;
  onSearchChange: (value: string) => void;
  onLoading?: boolean;
  defaultChecked?: string;
  labelValueKey?: string;
  clearable?: boolean;
  truncateValue?: number;
  popOverClassName?: string;
  disabled?: boolean;
}

const ComboboxAsync = forwardRef<HTMLButtonElement, ComboboxAsyncProps>(
  (
    {
      buttonClassName,
      boxWrapperClassName,
      boxInputClassName,
      boxListClassName,
      boxGroupClassName,
      boxItemClassName,
      options,
      value,
      onChange,
      onSearchChange,
      onLoading,
      buttonPlaceholder,
      inputPlaceholder,
      labelValueKey = "value",
      clearable = true,
      truncateValue = 0,
      popOverClassName = "aero:z-[100] aero:border aero:border-neutral-200 aero:bg-white aero:shadow-lg aero:rounded-md",
      disabled = false,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearchChange = useCallback(
      debounce((value: string) => {
        onSearchChange(value);
      }, 1000),
      [onSearchChange]
    );

    const onHandleChangeSearchInput = (value: string) => {
      setSearch(value);
      debouncedSearchChange(value);
    };

    const clearInputAndSelection = () => {
      setSearch("");
      if (onChange) {
        onChange?.({ label: "", value: "" });
      }
    };

    const hasThingsToClear = (): boolean => {
      return !!value || !!search;
    };

    function truncate(str: string, maxLength: number) {
      if (!str) return "";
      return str.length > maxLength ? str.slice(0, maxLength - 1) + "…" : str;
    }

    const comboboxLabel = options.find((option) => {
      const key = labelValueKey as keyof ComboboxOption;
      return option[key] === value;
    })?.label;

    return (
      <Popover open={open} onOpenChange={setOpen} modal={true}>
        <div className="aero:relative aero:w-full">
          <PopoverTrigger className="aero:w-full">
            <Button
              ref={ref}
              type="button"
              intent="custom"
              design="outline"
              role="combobox"
              className={cn(
                "aero:w-full aero:justify-between aero:min-w-0 aero:text-left aero:rounded-md aero:border aero:border-neutral-300 aero:bg-white aero:shadow-sm aero:focus:ring-2 aero:focus:ring-brand-primary aero:active:ring-2 aero:active:ring-brand-primary aero:transition-all aero:duration-150 aero:h-10 aero:px-3 aero:text-sm aero:font-normal",
                !value && "aero:text-neutral-500",
                buttonClassName
              )}
              disabled={disabled}
            >
              <span className="aero:block aero:truncate aero:text-left aero:flex-1 aero:min-w-0">
                {value && !truncateValue
                  ? comboboxLabel
                  : value && truncateValue
                    ? truncate(comboboxLabel ?? "", truncateValue)
                    : buttonPlaceholder
                      ? buttonPlaceholder
                      : "Select Option"}
              </span>
              <ChevronsDownUp className="aero:ml-2 aero:h-4 aero:w-4 aero:shrink-0 aero:opacity-50" />
            </Button>
          </PopoverTrigger>
          {clearable && hasThingsToClear() && (
            <Button
              design="text"
              size="iconMd"
              className="aero:!rounded-full aero:absolute aero:right-4 aero:top-1/2 aero:-translate-y-1/2 aero:h-auto aero:w-auto aero:p-1 aero:z-10 aero:bg-gray-200"
              onClick={(e) => {
                e.stopPropagation();
                clearInputAndSelection();
              }}
            >
              <X size={10} />
            </Button>
          )}
        </div>

        <PopoverContent
          className={cn(
            popOverClassName,

            "aero:min-w-80 aero:max-w-96 aero:p-0"
          )}
          align="start"
        >
          <Command
            className={cn(
              "aero:bg-white aero:rounded-md aero:border aero:border-neutral-200 aero:shadow-sm",
              boxWrapperClassName
            )}
          >
            <CommandInput
              placeholder={
                inputPlaceholder ? inputPlaceholder : "Search item..."
              }
              className={cn(
                "aero:h-9 aero:rounded-md aero:border aero:border-neutral-300 aero:px-3 aero:text-sm aero:font-normal aero:mb-2",
                boxInputClassName
              )}
              value={search}
              onValueChange={onHandleChangeSearchInput}
            />
            <CommandList
              className={cn("aero:overflow-y-hidden", boxListClassName)}
            >
              <ScrollArea className="aero:h-72 aero:scrollbar-thin aero:scrollbar-thumb-gray-300 aero:scrollbar-track-gray-100">
                {onLoading ? (
                  <CommandEmpty className="aero:flex aero:flex-col aero:h-1/2 aero:justify-center aero:gap-2 aero:items-center aero:text-neutral-500">
                    <Loader
                      size={20}
                      className="aero:animate aero:animate-spin"
                    />
                    <span className="aero:text-sm aero:font-semibold">
                      Load data.
                    </span>
                  </CommandEmpty>
                ) : !options.length ? (
                  <CommandEmpty>No item found.</CommandEmpty>
                ) : (
                  <CommandGroup
                    className={cn("aero:py-1", boxGroupClassName)}
                    forceMount={true}
                  >
                    {options.map((option) => (
                      <CommandItem
                        className={cn(
                          "aero:cursor-pointer aero:px-3 aero:py-2 aero:text-sm aero:font-normal aero:rounded-md aero:transition-all aero:duration-150 hover:aero:bg-brand-primary-50 hover:aero:text-brand-primary-700 aero:flex aero:items-center",
                          "aero:py-1 aero:!z-[9999]",
                          option.value === value &&
                            "aero:bg-brand-primary-100 aero:text-brand-primary-700",
                          boxItemClassName
                        )}
                        value={option.value.toString()}
                        key={option.value}
                        onSelect={() => {
                          if (onChange) {
                            onChange(option);
                          }
                          setOpen(false);
                        }}
                      >
                        {option.label}
                        <Check
                          className={cn(
                            "aero:ml-auto aero:h-4 aero:w-4",
                            option.value === value
                              ? "aero:opacity-100"
                              : "aero:opacity-0"
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  }
);

export { ComboboxAsync };
